
import React, { useRef, useState, useEffect } from "react";
import MarketPlaceCards from "./MarketPlaceCards";
import api from '../api';
import { Box, TextField, Button, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// columns currently not used by MarketPlaceCards, but keep if needed elsewhere
// const columns = [
//   { field: "id_prod", headerName: "Code", width: 200 },
//   { field: "desc_prod_full", headerName: "Description", width: 500 },
//   { field: "sell_unit_std", headerName: "Sell Unit", width: 150 },
//   { field: "id_uom___1", headerName: "UOM", width: 100 },
//   { field: "id_gst_rate", headerName: "GST Rate", width: 100 }
// ];

const MUIDataGrid = () => {
  const inputRefs = {
    searchCriteria: useRef(null)
  };

  const [formValues, setFormValues] = useState({
    searchCriteria: ''
  });

  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (inputRefs.searchCriteria.current) {
      inputRefs.searchCriteria.current.focus();
    }
  }, []);

  // Initial load
  useEffect(() => {
    const getInitialData = async () => {
      try {
        setLoading(true);
        const apiURL = (window.APP_CONFIG?.API_URL || "") + "marketplace/mpproducts";
        const response = await api.get(apiURL);
        if (response.status === 200) {
          setProducts(response.data);
          setError(""); // clear any previous message
        }
      } catch (e) {
        console.log("Error fetching initial data:", e);
        setError("An error occurred while fetching products.");
      } finally {
        setLoading(false);
      }
    };
    getInitialData();
  }, []);

  const handleInputChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.searchCriteria?.trim()) {
      setError("Please enter search criteria.");
      setProducts([]);
      return;
    }

    setProducts([]);  // Clear to avoid lingering results
    setLoading(true);

    const jsonApiUrl = window.APP_CONFIG?.API_URL || "http://localhost:3000/";
    try {
      const apiURL = `${jsonApiUrl}marketplace/mpproducts?search=${encodeURIComponent(formValues.searchCriteria.trim())}`;
      const response = await api.get(apiURL);

      if (response.status === 200) {
        setProducts(response.data);
        if (response.data.length >= 500) {
          setError(`Last Search: '${formValues.searchCriteria}'  Result limited to 500 products. Please refine your search`);
        } else {
          setError(`Last Search: '${formValues.searchCriteria}'`);
        }
        setFormValues((prevValues) => ({
          ...prevValues,
          searchCriteria: "",
        }));
        if (inputRefs.searchCriteria.current) {
          inputRefs.searchCriteria.current.focus();
        }
      }
    } catch (error) {
      console.log("Search error:", error);
      setError("An error occurred while fetching products.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Clear Filters
  const handleClear = async () => {
    setFormValues({ searchCriteria: '' });
    setError('');
    setProducts([]);
    setLoading(true);

    try {
      const apiURL = (window.APP_CONFIG?.API_URL || "") + "marketplace/mpproducts";
      const response = await api.get(apiURL);
      if (response.status === 200) {
        setProducts(response.data);
      }
    } catch (e) {
      console.log("Error fetching data:", e);
      setError("An error occurred while fetching products.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
      {/* Metadata (these tags are usually placed in index.html, but keeping here as per your original) */}
      <title>Sunshine Market Place</title>
      <link rel="icon" type="image/x-icon" href="images/SunMarket.ico" />

      {/* Responsive hero image */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, sm: 2 } }}>
        <img
          src="images/SMP.png"
          alt="Sunshine Market Place"
          style={{
            maxWidth: '100%',
            height: 'auto',
            width: isXs ? '85%' : '50%',
          }}
        />
      </Box>

      {/* Search form */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        aria-label="Product search form"
      >
        <Stack
          direction={isXs ? 'column' : 'row'}
          spacing={isXs ? 1 : 2}
          alignItems={isXs ? 'stretch' : 'center'}
          sx={{ mb: { xs: 1.5, sm: 2 } }}
        >
          <TextField
            label="Search For"
            name="searchCriteria"
            inputRef={inputRefs.searchCriteria}
            value={formValues.searchCriteria}
            onChange={handleInputChange}
            fullWidth={isXs}
            size={isXs ? 'small' : 'medium'}
            aria-label="Search criteria"
            sx={{
              flexGrow: 1,
              maxWidth: { sm: 320, md: 480},
            }}
          />
          <Stack direction="row" spacing={isXs ? 1 : 2} sx={{ width: isXs ? '100%' : 'auto' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth={isXs}
              size={isXs ? 'small' : 'medium'}
            >
              Find
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={handleClear}
              fullWidth={isXs}
              size={isXs ? 'small' : 'medium'}
            >
              Clear Filter
            </Button>
          </Stack>
        </Stack>

        {/* Status / error message */}
        <Box sx={{ minHeight: isXs ? 20 : 24, mb: { xs: 1, sm: 1.5 } }}>
          {error ? (
            <Box component="p" sx={{ color: 'error.main', fontWeight: 600, m: 0 }}>
              {error}
            </Box>
          ) : (
            <Box component="p" sx={{ visibility: 'hidden', m: 0 }}>&nbsp;</Box>
          )}
        </Box>

        {/* Cards with mobile-friendly page size & loading */}
        <MarketPlaceCards
          rows={products}
          pageSize={10}    // desktop default, auto-reduced on mobile within MarketPlaceCards
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default MUIDataGrid;
